<template>
  <section class="page" data-testid="page-persona-detail">
    <header class="page-header">
      <div><h1>{{ name }}</h1><p>人设详情、草稿、发布、diff 和回滚。</p></div>
      <div class="actions">
        <RouterLink class="button" to="/personas">返回</RouterLink>
        <button class="button primary" :disabled="loading" @click="load">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>

    <div class="metrics-grid">
      <MetricCard label="RAG 片段" :value="String(stats.rag_count || 0)" />
      <MetricCard label="样本消息" :value="String(persona.sample_messages_total || 0)" />
      <MetricCard label="发布版本" :value="publishedRevision ? `#${publishedRevision.revision_no}` : '-'" />
      <MetricCard label="未发布草稿" :value="latestDraft ? `#${latestDraft.revision_no}` : '无'" />
    </div>
    <p v-if="error" class="error-text">{{ error }}</p>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>

    <div class="split">
      <div>
        <div class="panel">
          <h2>编辑草稿</h2>
          <p v-if="editingRevisionId">正在编辑草稿 #{{ editingRevisionNo }}</p>
          <p v-else>当前编辑内容会先保存为草稿，发布后才影响线上人设。</p>
          <FormField label="System Prompt"><textarea v-model="systemPrompt" /></FormField>
          <FormField label="风格分析"><textarea v-model="styleAnalysis" /></FormField>
          <FormField label="变更说明"><input v-model="changeSummary" placeholder="例如：收紧回复语气，减少过度解释" /></FormField>
          <div class="actions">
            <button class="button primary" :disabled="saving || Boolean(metadataError)" @click="saveDraft">{{ saving ? "处理中..." : "保存草稿" }}</button>
            <button class="button" :disabled="saving || !editingRevisionId" @click="showDiff(editingRevisionId)">查看 Diff</button>
            <button class="button primary" :disabled="saving || !editingRevisionId" @click="publishRevision(editingRevisionId)">发布草稿</button>
            <button class="button" :disabled="saving || Boolean(metadataError)" @click="publishImmediately">立即发布当前编辑</button>
          </div>
        </div>
        <div class="panel">
          <h2>样本消息</h2>
          <pre>{{ (persona.sample_messages || []).join("\n") || "暂无样本" }}</pre>
        </div>
      </div>

      <div>
        <div class="panel">
          <h2>Metadata</h2>
          <textarea v-model="metadataJson" />
          <p v-if="metadataError" class="error-text">{{ metadataError }}</p>
          <div class="actions">
            <button class="button primary" :disabled="saving || Boolean(metadataError)" @click="saveDraft">保存到草稿</button>
          </div>
        </div>
        <div class="panel">
          <h2>版本</h2>
          <div v-if="!revisions.length" class="empty">暂无版本记录。</div>
          <table v-else class="data-table">
            <thead>
              <tr>
                <th>版本</th>
                <th>状态</th>
                <th>来源</th>
                <th>说明</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="revision in revisions" :key="revision.id">
                <td>#{{ revision.revision_no }}</td>
                <td>{{ revision.status }}</td>
                <td>{{ revision.source }}</td>
                <td>{{ revision.change_summary }}</td>
                <td>
                  <button class="button" :disabled="saving" @click="loadRevisionIntoEditor(revision)">编辑视图</button>
                  <button class="button" :disabled="saving" @click="showDiff(revision.id)">Diff</button>
                  <button v-if="revision.status === 'draft'" class="button primary" :disabled="saving" @click="publishRevision(revision.id)">发布</button>
                  <button v-if="revision.status !== 'draft'" class="button" :disabled="saving" @click="rollbackRevision(revision.id)">回滚到此版</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="panel">
          <h2>学习配置</h2>
          <textarea v-model="learnConfigJson" />
          <p v-if="learnConfigError" class="error-text">{{ learnConfigError }}</p>
          <div class="actions">
            <button class="button primary" :disabled="saving || Boolean(learnConfigError)" @click="saveLearnConfig">保存学习配置</button>
            <button class="button" :disabled="saving" @click="forceStyle">重分析风格</button>
          </div>
        </div>
        <div class="panel">
          <h2>危险操作</h2>
          <button class="button danger" :disabled="saving" @click="remove">删除人设</button>
        </div>
      </div>
    </div>

    <div v-if="diffResult" class="panel">
      <div class="page-header">
        <div><h2>Diff</h2><p>基准版本 #{{ diffResult.base_revision?.revision_no }} → 目标版本 #{{ diffResult.target_revision?.revision_no }}</p></div>
        <button class="button" @click="diffResult = null">关闭</button>
      </div>
      <div class="split">
        <div>
          <h3>System Prompt</h3>
          <pre>{{ (diffResult.diff?.system_prompt || []).join("\n") || "无变化" }}</pre>
          <h3>风格分析</h3>
          <pre>{{ (diffResult.diff?.style_raw_analysis || []).join("\n") || "无变化" }}</pre>
        </div>
        <div>
          <h3>Metadata</h3>
          <pre>{{ JSON.stringify(diffResult.diff?.metadata || [], null, 2) }}</pre>
          <h3>样本消息</h3>
          <pre>{{ JSON.stringify(diffResult.diff?.sample_messages || {}, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { api, postJson } from "../api/client";
import { useToastStore } from "../stores/toast";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";

const route = useRoute();
const router = useRouter();
const toast = useToastStore();
const name = String(route.params.name || "");
const persona = ref<Record<string, any>>({});
const stats = ref<Record<string, any>>({});
const systemPrompt = ref("");
const styleAnalysis = ref("");
const metadataJson = ref("{}");
const learnConfigJson = ref("{}");
const changeSummary = ref("");
const revisions = ref<any[]>([]);
const publishedRevision = ref<any | null>(null);
const latestDraft = ref<any | null>(null);
const editingRevisionId = ref<number | null>(null);
const diffResult = ref<any | null>(null);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
const actionResult = ref("");
const editingRevisionNo = computed(() => revisions.value.find((item) => item.id === editingRevisionId.value)?.revision_no || "");
const metadataError = computed(() => jsonObjectError(metadataJson.value, "Metadata"));
const learnConfigError = computed(() => jsonObjectError(learnConfigJson.value, "学习配置"));

function parseJsonObject(raw: string, label: string) {
  const value = JSON.parse(raw || "{}");
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} 必须是 JSON 对象`);
  }
  return value;
}

function jsonObjectError(raw: string, label: string) {
  try {
    parseJsonObject(raw, label);
    return "";
  } catch (err) {
    return err instanceof Error ? err.message : `${label} 格式错误`;
  }
}

function snapshotFromEditor() {
  return {
    system_prompt: systemPrompt.value,
    style: { raw_analysis: styleAnalysis.value },
    metadata: parseJsonObject(metadataJson.value, "Metadata"),
    change_summary: changeSummary.value || "Manual persona draft"
  };
}

function loadSnapshot(snapshot: Record<string, any>) {
  systemPrompt.value = snapshot.system_prompt || "";
  styleAnalysis.value = snapshot.style?.raw_analysis || "";
  metadataJson.value = JSON.stringify(snapshot.metadata || {}, null, 2);
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const payload = await api<{ persona: Record<string, any>; stats: Record<string, any>; learn_config: any; published_revision?: any; latest_draft_revision?: any }>(`/api/persona/${encodeURIComponent(name)}/detail`);
    persona.value = payload.persona || {};
    stats.value = payload.stats || {};
    publishedRevision.value = payload.published_revision || null;
    latestDraft.value = payload.latest_draft_revision || null;
    const editSource = latestDraft.value?.snapshot || {
      system_prompt: persona.value.system_prompt || "",
      style: { raw_analysis: persona.value.style_analysis || "" },
      metadata: persona.value.metadata || {}
    };
    editingRevisionId.value = latestDraft.value?.id || null;
    changeSummary.value = latestDraft.value?.change_summary || "";
    loadSnapshot(editSource);
    learnConfigJson.value = JSON.stringify(payload.learn_config || {}, null, 2);
    await loadRevisions();
  } catch (err) {
    error.value = errorText(err, "人设详情加载失败");
  } finally {
    loading.value = false;
  }
}

async function loadRevisions() {
  const payload = await api<any>(`/api/persona/${encodeURIComponent(name)}/revisions`);
  revisions.value = payload.revisions || [];
  publishedRevision.value = payload.published_revision || publishedRevision.value;
  latestDraft.value = payload.latest_draft_revision || null;
}

function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback;
}

async function runAction(label: string, operation: () => Promise<any>, reload = true) {
  saving.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const result = await operation();
    actionResult.value = label;
    toast.push(label);
    if (reload) await load();
    return result;
  } catch (err) {
    error.value = errorText(err, `${label}失败`);
    toast.push(error.value, "error");
    return null;
  } finally {
    saving.value = false;
  }
}

async function saveDraft() {
  if (metadataError.value) {
    toast.push(metadataError.value, "error");
    return;
  }
  await runAction("草稿已保存", async () => {
    const body = snapshotFromEditor();
    if (editingRevisionId.value) {
      const payload = await api<any>(`/api/persona/${encodeURIComponent(name)}/revisions/${editingRevisionId.value}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      latestDraft.value = payload.revision;
    } else {
      const payload = await postJson<any>(`/api/persona/${encodeURIComponent(name)}/revisions`, body);
      latestDraft.value = payload.revision;
      editingRevisionId.value = payload.revision.id;
    }
    await loadRevisions();
  }, false);
}

async function publishRevision(revisionId: number | null) {
  if (!revisionId) return;
  const payload = await runAction("草稿已发布", () => postJson<any>(`/api/persona/${encodeURIComponent(name)}/revisions/${revisionId}/publish`, {}));
  if (payload) {
    actionResult.value = `已发布版本 #${payload.revision_no}`;
    diffResult.value = null;
  }
}

async function publishImmediately() {
  if (metadataError.value) {
    toast.push(metadataError.value, "error");
    return;
  }
  if (!window.confirm("立即发布会绕过草稿审核，确定继续？")) return;
  await runAction("当前编辑已立即发布", () => postJson(`/api/persona/${encodeURIComponent(name)}/update`, {
    system_prompt: systemPrompt.value,
    style_analysis: styleAnalysis.value,
    metadata: parseJsonObject(metadataJson.value, "Metadata"),
    change_summary: changeSummary.value || "Immediate publish from editor"
  }));
}

async function showDiff(revisionId: number | null) {
  if (!revisionId) return;
  saving.value = true;
  error.value = "";
  try {
    diffResult.value = await api<any>(`/api/persona/${encodeURIComponent(name)}/revisions/${revisionId}/diff?base=published`);
  } catch (err) {
    error.value = errorText(err, "Diff 加载失败");
  } finally {
    saving.value = false;
  }
}

function loadRevisionIntoEditor(revision: any) {
  editingRevisionId.value = revision.status === "draft" ? revision.id : null;
  changeSummary.value = revision.change_summary || "";
  loadSnapshot(revision.snapshot || {});
}

async function rollbackRevision(revisionId: number) {
  if (!window.confirm("确定从该版本创建新版本并发布？")) return;
  const payload = await runAction("已回滚并发布", () => postJson<any>(`/api/persona/${encodeURIComponent(name)}/revisions/${revisionId}/rollback`, {}));
  if (payload) actionResult.value = `已回滚并发布版本 #${payload.revision_no}`;
}

async function saveLearnConfig() {
  if (learnConfigError.value) {
    toast.push(learnConfigError.value, "error");
    return;
  }
  await runAction("学习配置已保存", () => postJson(`/api/persona/${encodeURIComponent(name)}/learn_config`, parseJsonObject(learnConfigJson.value, "学习配置")));
}

async function forceStyle() {
  await runAction("已触发风格重分析", () => postJson(`/api/persona/${encodeURIComponent(name)}/force_style`, {}));
}

async function remove() {
  if (!window.confirm(`确定删除人设 ${name}？`)) return;
  const ok = await runAction("人设已删除", () => postJson(`/api/persona/${encodeURIComponent(name)}/delete`, {}), false);
  if (ok) await router.push("/personas");
}
onMounted(load);
</script>

<style scoped>
.save-result {
  border: 1px solid #386f47;
  background: #142016;
  color: var(--success);
  border-radius: 8px;
  padding: 10px 12px;
}
</style>
